import { useState, useEffect, useMemo } from 'react';
import EntityLink from '../components/EntityLink';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton from '../components/common/ClearFiltersButton';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';

export default function TimesheetsPage() {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const matchFilter = get('match', 'all');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const [showImport, setShowImport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ contractor_id: '', period_start: '', period_end: '', hours: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  function load() {
    setError(false);
    api.get('/timesheets').then(r => setTimesheets(r.data)).catch(() => setError(true));
  }

  useEffect(() => {
    load();
    api.get('/contractors').then(r => setContractors(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (!timesheets) return [];
    if (matchFilter === 'all') return timesheets;
    return timesheets.filter(t => t.hours_match_status === matchFilter);
  }, [timesheets, matchFilter]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  async function handleSubmit() {
    if (!form.contractor_id || !form.period_start || !form.period_end || !form.hours) {
      setFormError('Contractor, period, and hours are required');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await api.post('/timesheets', { ...form, contractor_id: Number(form.contractor_id), hours: Number(form.hours) });
      setForm({ contractor_id: '', period_start: '', period_end: '', hours: '', notes: '' });
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Could not save timesheet');
    } finally {
      setSaving(false);
    }
  }

  async function handleExport() {
    await downloadCsv('/timesheets/export', 'timesheets-export.csv', exportIdsParam(filtered, timesheets));
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Timesheets {timesheets && <span className="page-title-count">({timesheets.length} total)</span>}
          </h1>
          <p className="page-subtitle">Raw hours by contractor and period — compared against Purchase Order approved hours.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setShowForm(s => !s)}>+ Log Timesheet</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
          {formError && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              {formError}
            </div>
          )}
          <div className="form-grid">
            <div className="form-field">
              <label>Contractor</label>
              <select value={form.contractor_id} onChange={e => setForm(f => ({ ...f, contractor_id: e.target.value }))}>
                <option value="">Select…</option>
                {contractors.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Hours</label>
              <input type="number" step="0.01" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Period start</label>
              <input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Period end</label>
              <input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} />
            </div>
            <div className="form-field span-2">
              <label>Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save Timesheet'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <select value={matchFilter} onChange={e => set({ match: e.target.value, page: 1 })}>
          <option value="all">All</option>
          <option value="matched">Matched</option>
          <option value="mismatch">Mismatch</option>
          <option value="pending">Pending (no PO yet)</option>
        </select>
        <ClearFiltersButton active={matchFilter !== 'all'} onClear={() => set({ match: '', page: 1 })} />
      </div>

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && timesheets === null && <StateBlock type="loading" />}
      {!error && timesheets !== null && filtered.length === 0 && <StateBlock type="noResults" />}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>Period</th>
                <th>Timesheet Hours</th>
                <th>Matched PO</th>
                <th>PO Approved Hours</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(t => (
                <tr key={t.id}>
                  <td><EntityLink type="contractor" id={t.contractor_id}>{t.contractor_name}</EntityLink></td>
                  <td>{t.period_start} – {t.period_end}</td>
                  <td>{t.hours}</td>
                  <td>
                    {t.matched_po
                      ? <EntityLink type="purchase_order" id={t.matched_po.id}>{t.matched_po.po_number}</EntityLink>
                      : '—'}
                  </td>
                  <td>{t.matched_po?.approved_hours ?? t.matched_po?.timesheet_hours ?? '—'}</td>
                  <td>
                    <Badge variant={t.hours_match_status === 'matched' ? 'success' : t.hours_match_status === 'mismatch' ? 'danger' : 'warning'}>
                      {t.hours_match_status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: 1 })}
          />
        </>
      )}

      {showImport && (
        <CsvImportModal
          entityLabel="Timesheet"
          importPath="/timesheets/import"
          onClose={() => setShowImport(false)}
          onComplete={load}
        />
      )}
    </div>
  );
}
